import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Card, Button, Alert, Spinner, Badge, Input, Select, Row, Col, Tabs, Field, ProgressBar } from '../../../../core/ui';
import {
  listInstallments,
  updateInstallment,
  deleteInstallment,
  createManualInstallment,
  listCards,
} from '../../api/budget';
import { formatTry } from '../../format';
import { useTranslation } from '../../../../core/context/TranslationContext';
import { useToast } from '../../../../core/context/ToastContext';
import { formatShortDate, formatLongMonth, monthKey, dateSortValue } from '../../../../core/format/date';
import SectionedTable from '../../../../core/common/SectionedTable';
import DuplicatesModal from './DuplicatesModal';
import styles from './InstallmentList.module.css';

const emptyManual = () => ({
  cardId: '',
  merchantName: '',
  totalAmount: '',
  installmentCount: 12,
  firstDueMonth: '',
  notes: '',
});

const shiftMonth = (mk, delta) => {
  const [y, m] = mk.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const isActiveInMonth = (inst, mk) => {
  if (inst.completed || !inst.nextDueMonth || inst.remainingCount <= 0) return false;
  const [ny, nm] = inst.nextDueMonth.split('-').map(Number);
  const nextDue = ny * 12 + (nm - 1);
  const [ty, tm] = mk.split('-').map(Number);
  const target = ty * 12 + (tm - 1);
  const lastDue = nextDue + inst.remainingCount - 1;
  return nextDue <= target && target <= lastDue;
};

const InstallmentList = () => {
  const { t, language } = useTranslation();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMonth = searchParams.get('month'); // "YYYY-MM" or null
  const [state, setState] = useState('open');
  const [items, setItems] = useState([]);
  const [cardsById, setCardsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manual, setManual] = useState(emptyManual());
  const [showManualForm, setShowManualForm] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, cards] = await Promise.all([listInstallments(state), listCards()]);
      setItems(list);
      const m = {};
      for (const c of cards) m[c.id] = c;
      setCardsById(m);
    } catch (err) {
      setError(err?.message || t('error'));
    } finally {
      setLoading(false);
    }
  }, [state, t]);

  useEffect(() => { load(); }, [load]);

  const toggleCompleted = async (i) => {
    try {
      await updateInstallment(i.id, { completed: !i.completed });
      toast(t(i.completed ? 'budget.toastReopened' : 'budget.toastCompleted'), {
        variant: i.completed ? 'info' : 'success',
      });
      load();
    } catch (err) {
      setError(err?.message || t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('budget.confirmDeleteInstallment'))) return;
    try {
      await deleteInstallment(id);
      toast(t('budget.toastDeleted'), { variant: 'danger' });
      load();
    } catch (err) {
      setError(err?.message || t('error'));
    }
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      await createManualInstallment({
        cardId: manual.cardId ? Number(manual.cardId) : null,
        merchantName: manual.merchantName,
        totalAmount: Number(manual.totalAmount),
        installmentCount: Number(manual.installmentCount),
        firstDueMonth: manual.firstDueMonth,
        notes: manual.notes || null,
      });
      setManual(emptyManual());
      setShowManualForm(false);
      load();
    } catch (err) {
      setError(err?.message || t('error'));
    }
  };

  const cardLabel = (cardId) => {
    const c = cardsById[cardId];
    if (!c) return '—';
    return `${c.bank || ''}${c.last4 ? ` ****${c.last4}` : ''}`.trim() || '—';
  };

  const columns = [
    {
      key: 'merchant',
      header: t('budget.merchant'),
      accessor: (r) => r.merchantNormalized || '',
      cell: (r) => (
        <div>
          <div className={styles.merchantCell}>{r.merchantNormalized}</div>
          {r.notes && <div className={styles.notesCell}>{r.notes}</div>}
        </div>
      ),
    },
    {
      key: 'card',
      header: t('budget.card'),
      accessor: (r) => cardLabel(r.cardId),
      cell: (r) => <span className={styles.infoCell}>{cardLabel(r.cardId)}</span>,
    },
    {
      key: 'totalAmount',
      header: t('budget.totalAmount'),
      align: 'end',
      accessor: (r) => Number(r.totalAmount),
      cell: (r) => formatTry(r.totalAmount),
    },
    {
      key: 'monthlyAmount',
      header: t('budget.monthlyAmount'),
      align: 'end',
      accessor: (r) => Number(r.monthlyAmount),
      cell: (r) => formatTry(r.monthlyAmount),
    },
    {
      key: 'progress',
      header: t('budget.progress'),
      accessor: (r) => (r.installmentCount - r.remainingCount) / Math.max(r.installmentCount, 1),
      cell: (r) => {
        const paidCount = r.paidCount ?? r.installmentCount - r.remainingCount;
        const progressPct = Math.round((paidCount / Math.max(r.installmentCount, 1)) * 100);
        return (
          <div className={styles.progressCell}>
            <Badge variant={r.completed ? 'neutral' : 'accent'}>
              {paidCount}/{r.installmentCount}
            </Badge>
            <div className={styles.miniProgress}>
              <ProgressBar now={progressPct} />
            </div>
            {!r.completed && r.unconfirmedMonths > 0 && (
              <span
                className={styles.unconfirmedText}
                title={t('budget.unconfirmedMonths', { count: r.unconfirmedMonths })}
              >
                {t('budget.unconfirmedMonths', { count: r.unconfirmedMonths })}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'nextDue',
      header: t('budget.nextDue'),
      accessor: (r) => dateSortValue(r.nextDueMonth),
      cell: (r) =>
        r.nextDueMonth && !r.completed ? (
          <span>{formatShortDate(r.nextDueMonth, language)}</span>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      align: 'end',
      cell: (r) => (
        <div className={styles.rowActions}>
          <Button size="sm" variant="secondary" onClick={() => toggleCompleted(r)}>
            {r.completed ? t('budget.reopen') : t('budget.markCompleted')}
          </Button>
          <Button size="sm" variant="dangerGhost" onClick={() => handleDelete(r.id)}>
            {t('delete')}
          </Button>
        </div>
      ),
    },
  ];

  const itemsForMonth = useMemo(() => {
    if (!selectedMonth || state !== 'open') return null;
    return items.filter((i) => isActiveInMonth(i, selectedMonth));
  }, [items, selectedMonth, state]);

  const monthGroupBy = {
    key: (row) => monthKey(row.nextDueMonth) || '0000-00',
    label: (_row, key) => (key === '0000-00' ? t('budget.completed') : formatLongMonth(key + '-01', language)),
    order: (a, b) => a.localeCompare(b),
    trailing: (rows) => {
      const sum = rows.reduce((acc, r) => acc + Number(r.monthlyAmount || 0), 0);
      return `${t('budget.totalDueForMonth')}: ${formatTry(sum)}`;
    },
  };

  // Expand each open installment into one virtual row per remaining payment
  // so the "tüm aylar" view shows all future months, not just each installment's nextDueMonth.
  // The backend already rolls progress forward to the current month, so every
  // row's nextDueMonth is this month or later.
  const expandedUpcoming = useMemo(() => {
    const rows = [];
    for (const i of items) {
      const baseMk = monthKey(i.nextDueMonth);
      if (!baseMk || i.remainingCount <= 0) {
        rows.push({ ...i, _mk: baseMk || '0000-00' });
        continue;
      }
      for (let j = 0; j < i.remainingCount; j++) {
        rows.push({ ...i, _mk: shiftMonth(baseMk, j) });
      }
    }
    return rows;
  }, [items]);

  const expandedMonthGroupBy = useMemo(() => ({
    key: (row) => row._mk || '0000-00',
    label: (_row, key) => (key === '0000-00' ? t('budget.completed') : formatLongMonth(key + '-01', language)),
    order: (a, b) => a.localeCompare(b),
    trailing: (rows) => {
      const sum = rows.reduce((acc, r) => acc + Number(r.monthlyAmount || 0), 0);
      return `${t('budget.totalDueForMonth')}: ${formatTry(sum)}`;
    },
  }), [t, language]);

  const rowClassName = (r) => (r.completed ? styles.completedRow : '');

  const renderTabContent = () => {
    if (state === 'open') {
      if (selectedMonth && itemsForMonth !== null) {
        const total = itemsForMonth.reduce((acc, r) => acc + Number(r.monthlyAmount || 0), 0);
        if (itemsForMonth.length === 0) {
          return (
            <Card>
              <Card.Body className={styles.emptyCard}>{t('budget.noInstallmentsForMonth')}</Card.Body>
            </Card>
          );
        }
        return (
          <div className={styles.section}>
            <SectionedTable
              data={itemsForMonth}
              columns={columns}
              rowKey={(r) => r.id}
              rowClassName={rowClassName}
              initialSort={{ key: 'merchant', dir: 'asc' }}
              emptyMessage={t('budget.noInstallmentsForMonth')}
            />
            <div className={styles.monthTotalRow}>
              {t('budget.totalDueForMonth')}: <strong>{formatTry(total)}</strong>
            </div>
          </div>
        );
      }

      if (items.length === 0) {
        return (
          <Card>
            <Card.Body className={styles.emptyCard}>{t('budget.noInstallments')}</Card.Body>
          </Card>
        );
      }

      return (
        <div className={styles.section}>
          <SectionedTable
            data={expandedUpcoming}
            columns={columns}
            rowKey={(r) => `${r.id}-${r._mk}`}
            rowClassName={rowClassName}
            groupBy={expandedMonthGroupBy}
            initialSort={{ key: 'merchant', dir: 'asc' }}
            emptyMessage={t('budget.noInstallments')}
          />
        </div>
      );
    }

    return (
      <SectionedTable
        data={items}
        columns={columns}
        rowKey={(r) => r.id}
        rowClassName={rowClassName}
        groupBy={state === 'all' ? monthGroupBy : null}
        initialSort={{ key: 'nextDue', dir: 'asc' }}
        emptyMessage={t('budget.noInstallments')}
      />
    );
  };

  return (
    <Container>
      <div className={styles.pageHeader}>
        <h2 className={styles.pageTitle}>{t('budget.installments')}</h2>
        <div className={styles.headerActions}>
          <Button variant="secondary" onClick={() => setShowDuplicates(true)}>
            {t('budget.findDuplicates')}
          </Button>
          <Button variant="primary" onClick={() => setShowManualForm(!showManualForm)}>
            {t('budget.addInstallmentManual')}
          </Button>
        </div>
      </div>

      <DuplicatesModal
        show={showDuplicates}
        onHide={() => setShowDuplicates(false)}
        onApplied={load}
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {showManualForm && (
        <Card className={styles.formCard}>
          <Card.Header>{t('budget.addInstallmentManual')}</Card.Header>
          <Card.Body>
            <form onSubmit={handleManualSubmit}>
              <Row gutter="md" className={styles.formRow}>
                <Col span={3}>
                  <Field label={t('budget.card')}>
                    <Select
                      value={manual.cardId}
                      onChange={(e) => setManual({ ...manual, cardId: e.target.value })}
                    >
                      <option value="">{t('budget.pickCardOptional')}</option>
                      {Object.values(cardsById).map(c => (
                        <option key={c.id} value={c.id}>
                          {c.bank} {c.last4 ? `****${c.last4}` : ''}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </Col>
                <Col span={3}>
                  <Field label={t('budget.merchant')}>
                    <Input
                      value={manual.merchantName}
                      onChange={(e) => setManual({ ...manual, merchantName: e.target.value })}
                      required
                    />
                  </Field>
                </Col>
                <Col span={2}>
                  <Field label={t('budget.totalAmount')}>
                    <Input
                      type="number"
                      step="0.01"
                      value={manual.totalAmount}
                      onChange={(e) => setManual({ ...manual, totalAmount: e.target.value })}
                      required
                    />
                  </Field>
                </Col>
                <Col span={1}>
                  <Field label={t('budget.count')}>
                    <Input
                      type="number"
                      min={2}
                      max={48}
                      value={manual.installmentCount}
                      onChange={(e) => setManual({ ...manual, installmentCount: e.target.value })}
                      required
                    />
                  </Field>
                </Col>
                <Col span={2}>
                  <Field label={t('budget.firstDueMonth')}>
                    <Input
                      type="date"
                      value={manual.firstDueMonth}
                      onChange={(e) => setManual({ ...manual, firstDueMonth: e.target.value })}
                      required
                    />
                  </Field>
                </Col>
                <Col span={1}>
                  <Button type="submit" className={styles.submitBtn}>{t('budget.add')}</Button>
                </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>
      )}

      <div className={styles.controls}>
        <Tabs variant="pills" className={styles.tabs}>
          <Tabs.Tab active={state === 'open'} onClick={() => setState('open')}>
            {t('budget.open')}
          </Tabs.Tab>
          <Tabs.Tab active={state === 'completed'} onClick={() => setState('completed')}>
            {t('budget.completed')}
          </Tabs.Tab>
          <Tabs.Tab active={state === 'all'} onClick={() => setState('all')}>
            {t('budget.all')}
          </Tabs.Tab>
        </Tabs>

        {state === 'open' && (
          <div className={styles.monthNav}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSearchParams({ month: shiftMonth(selectedMonth || currentMonthKey(), -1) }, { replace: true })}
            >
              ‹
            </Button>
            <span className={styles.monthLabel}>
              {selectedMonth
                ? formatLongMonth(selectedMonth + '-01', language)
                : t('budget.allMonths')}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSearchParams({ month: shiftMonth(selectedMonth || currentMonthKey(), 1) }, { replace: true })}
            >
              ›
            </Button>
            {selectedMonth && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setSearchParams({}, { replace: true })}
              >
                {t('budget.allMonths')}
              </Button>
            )}
          </div>
        )}
      </div>

      {loading ? <Spinner label={t('loading')} /> : renderTabContent()}
    </Container>
  );
};

export default InstallmentList;
